import {
  areAllTicketsSoldOut,
  getEarliestScheduledTicketDefinition,
  getTicketDefinitionsPriceRange,
  isRegistrationClosedVisible,
  saleScheduledAndHasTickets,
  saleStartedAndHasTickets,
  TicketWithFormattedSalePeriod,
} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Text} from '@wix/wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {useCurrencyFormatter} from '../../../../../../../commons/hooks/currency'
import {useAppActions, useAppSelector} from '../../../../hooks/runtime'
import {getEvent, isCheckoutAvailable} from '../../../../selectors/event'
import {isCheckoutActionPending} from '../../../../selectors/pending-requests'
import {getTickets} from '../../../../selectors/tickets'
import {SeatingPlanModal} from '../../../chunks/seating-plan-modal'
import {RegistrationButtonSkin} from '../../../registration-buttons/registration-button-skin'
import {Badge} from '../fields/badge'
import {Price} from '../fields/price'
import s from './seating-tickets.scss'
import {classes} from './seatings-tickets.st.css'

const PRICE_RANGE_ID = 'price-range'

export const SeatingTickets = () => {
  const {t} = useTranslation()
  const {isMobile} = useEnvironment()
  const {getFormattedMoney} = useCurrencyFormatter()
  const {openSeatingPlan} = useAppActions()
  const checkoutAvailable = useAppSelector(isCheckoutAvailable)
  const checkoutActionPending = useAppSelector(isCheckoutActionPending)
  const event = useAppSelector(getEvent)
  const tickets = useAppSelector(getTickets)
  const priceRange = getTicketDefinitionsPriceRange(tickets)

  const soldOut = areAllTicketsSoldOut(tickets)
  const registrationClosed = isRegistrationClosedVisible(event)
  const ticketsOnSale = tickets.some(ticket => saleStartedAndHasTickets(ticket))
  const scheduledAvailable = tickets.some(ticket => saleScheduledAndHasTickets(ticket))

  const price = priceRange
    ? priceRange.min.value === priceRange.max.value
      ? getFormattedMoney(priceRange.min)
      : t('tickets.pricingOption.range', {
          min: getFormattedMoney(priceRange.min),
          max: getFormattedMoney(priceRange.max),
        })
    : null

  const openSeatingPlanModal = () => {
    if (!checkoutAvailable) {
      return
    }

    SeatingPlanModal.preload()
    return openSeatingPlan()
  }

  const renderStatus = () => {
    if (soldOut) {
      return (
        <div className={s.badge}>
          <Badge dataHook={DH.TICKET_SOLD_OUT}>{t('mobile.ticketsPicker.soldOut')}</Badge>
        </div>
      )
    }

    if (registrationClosed) {
      return (
        <div className={s.badge}>
          <Badge dataHook={DH.TICKET_SALE_ENDED}>{t('ticketsPicker.saleEndedBadge')}</Badge>
        </div>
      )
    }

    if (ticketsOnSale) {
      return (
        <div className={s.button}>
          <RegistrationButtonSkin
            spinner={checkoutActionPending}
            secondary={true}
            minHeight={isMobile ? undefined : '42px'}
            onClick={openSeatingPlanModal}
            dataHook={isMobile ? DH.CHECKOUT : DH.RESERVE_TICKETS_BUTTON}
            narrowerButton={isMobile}
            text={t('ticketsPicker_selectTickets')}
          />
        </div>
      )
    }

    if (scheduledAvailable) {
      const earliestTicket = getEarliestScheduledTicketDefinition(tickets) as TicketWithFormattedSalePeriod | null

      if (earliestTicket) {
        return (
          <Text
            tagName="div"
            className={classNames(s.goesOnSale, classes.secondaryText)}
            role="group"
            data-hook={DH.TICKET_GOES_ON_SALE}
          >
            <p>{t('ticketsPicker.goesOnSale')}</p>
            <p>{earliestTicket.salePeriod.formattedSaleStartDate}</p>
          </Text>
        )
      }
    }

    return (
      <div className={s.badge}>
        <Badge dataHook={DH.TICKET_SALE_ENDED}>{t('ticketsPicker.saleEndedBadge')}</Badge>
      </div>
    )
  }

  return (
    <div
      data-hook={DH.TICKETS_PICKER}
      id={DH.TICKETS_PICKER}
      className={classNames(s.container, {[s.mobile]: isMobile})}
    >
      {price ? (
        <div className={s.price}>
          <Price label={t('ticketsThankYou.price')} id={PRICE_RANGE_ID} price={price} />
        </div>
      ) : null}
      {renderStatus()}
    </div>
  )
}
